import React, { lazy, Suspense } from 'react';
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { HelmetProvider } from 'react-helmet-async';
import { AuthProvider } from "@/contexts/AuthContext";
import { GeoBlocker } from "@/components/GeoBlocker";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import { AdminRoute } from "@/components/auth/AdminRoute";
import { LoadingFallback } from "@/components/LoadingFallback";
import { LanguageRouter } from "@/components/routing/LanguageRouter";
import "@/lib/i18n";

// Public pages - loaded immediately
import Index from "./pages/Index";
import Auth from "./pages/Auth";
import Privacy from "./pages/Privacy";
import Tariffs from "./pages/Tariffs";
import SeoPage from "./pages/SeoPage";
import NotFound from "./pages/NotFound";
import AirbnbCleaningSchedule from "./pages/AirbnbCleaningSchedule";
import StopDoubleBookings from "./pages/StopDoubleBookings";
import TurnoverBnBAlternative from "./pages/TurnoverBnBAlternative";
import AirbnbTurnoverChecklist from "./pages/AirbnbTurnoverChecklist";
import CleanerSchedulingTool from "./pages/CleanerSchedulingTool";
import Terms from "./pages/Terms";

// Protected pages - lazy loaded
const Portfolio = lazy(() => import("./pages/Portfolio"));
const Properties = lazy(() => import("./pages/Properties"));
const PropertyDetail = lazy(() => import("./pages/PropertyDetail"));
const Leases = lazy(() => import("./pages/Leases"));
const LeaseDetail = lazy(() => import("./pages/LeaseDetail"));
const Tenants = lazy(() => import("./pages/Tenants"));
const Tasks = lazy(() => import("./pages/Tasks"));
const Income = lazy(() => import("./pages/Income"));
const Expenses = lazy(() => import("./pages/Expenses"));
const Analytics = lazy(() => import("./pages/Analytics"));
const Settings = lazy(() => import("./pages/Settings"));
const Ads = lazy(() => import("./pages/Ads"));
const Mortgage = lazy(() => import("./pages/Mortgage"));
const MarketRange = lazy(() => import("./pages/MarketRange"));
const SeoManager = lazy(() => import("./pages/SeoManager"));

// Heavy pages - lazy loaded (contains large libraries)
const Calendar = lazy(() => import("./pages/Calendar"));
const Exports = lazy(() => import("./pages/Exports"));

const queryClient = new QueryClient();

const App = () => (
  <HelmetProvider>
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
            <LanguageRouter>
              <Suspense fallback={<LoadingFallback />}>
                <Routes>
                {/* Public routes */}
                <Route path="/auth" element={<Auth />} />
                <Route path="/privacy" element={<Privacy />} />
                <Route path="/terms" element={<Terms />} />
                <Route path="/tariffs" element={<Tariffs />} />
                <Route path="/tools/:slug" element={<SeoPage />} />
                <Route path="/guides/:slug" element={<SeoPage />} />
                <Route path="/locations/:slug" element={<SeoPage />} />
                
                {/* Pain-focused landing pages with language prefixes - MUST be before /:lang */}
                <Route path="/:lang/cleaner-scheduling-tool-for-airbnb-hosts" element={<CleanerSchedulingTool />} />
                <Route path="/:lang/airbnb-turnover-checklist-free-download" element={<AirbnbTurnoverChecklist />} />
                <Route path="/:lang/airbnb-cleaning-schedule-software" element={<AirbnbCleaningSchedule />} />
                <Route path="/:lang/stop-airbnb-double-bookings" element={<StopDoubleBookings />} />
                <Route path="/:lang/turnoverbnb-alternative" element={<TurnoverBnBAlternative />} />
                
                {/* Redirects for old URLs without language prefix */}
                <Route path="/cleaner-scheduling-tool-for-airbnb-hosts" element={<Navigate to="/en/cleaner-scheduling-tool-for-airbnb-hosts" replace />} />
                <Route path="/airbnb-turnover-checklist-free-download" element={<Navigate to="/en/airbnb-turnover-checklist-free-download" replace />} />
                <Route path="/airbnb-cleaning-schedule-software" element={<Navigate to="/en/airbnb-cleaning-schedule-software" replace />} />
                <Route path="/stop-airbnb-double-bookings" element={<Navigate to="/en/stop-airbnb-double-bookings" replace />} />
                <Route path="/turnoverbnb-alternative" element={<Navigate to="/en/turnoverbnb-alternative" replace />} />
                
                {/* Protected routes */}
                <Route
                  path="/portfolio"
                  element={
                    <ProtectedRoute>
                      <Portfolio />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/properties"
                  element={
                    <ProtectedRoute>
                      <Properties />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/properties/:id"
                  element={
                    <ProtectedRoute>
                      <PropertyDetail />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/leases"
                  element={
                    <ProtectedRoute>
                      <Leases />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/leases/:id"
                  element={
                    <ProtectedRoute>
                      <LeaseDetail />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/tenants"
                  element={
                    <ProtectedRoute>
                      <Tenants />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/tasks"
                  element={
                    <ProtectedRoute>
                      <Tasks />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/calendar"
                  element={
                    <ProtectedRoute>
                      <Calendar />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/income"
                  element={
                    <ProtectedRoute>
                      <Income />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/expenses"
                  element={
                    <ProtectedRoute>
                      <Expenses />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/analytics"
                  element={
                    <ProtectedRoute>
                      <Analytics />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/exports"
                  element={
                    <ProtectedRoute>
                      <Exports />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/ads"
                  element={
                    <ProtectedRoute>
                      <Ads />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/settings"
                  element={
                    <ProtectedRoute>
                      <Settings />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/mortgage"
                  element={
                    <ProtectedRoute>
                      <Mortgage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/market-range"
                  element={
                    <ProtectedRoute>
                      <MarketRange />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/seo"
                  element={
                    <ProtectedRoute>
                      <AdminRoute>
                        <SeoManager />
                      </AdminRoute>
                    </ProtectedRoute>
                  }
                />
                
                {/* Multi-language routes for main landing page - MUST be at the END before 404 */}
                <Route path="/" element={<Index />} />
                <Route path="/:lang" element={<Index />} />
                
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
            </LanguageRouter>
          </BrowserRouter>
        </TooltipProvider>
      </AuthProvider>
    </QueryClientProvider>
  </HelmetProvider>
);

export default App;
